import { ChangeEvent, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { handleTrack } from '../../../../../../../utils/tracker';
import { Button } from '../../../../../../Atoms/Button';
import {
  emailValidator,
  phoneValidator,
} from '../../../../../../Webinars/Common/WebinarLeadForm/Common/validator';
import { sendPricingPageLead } from '../../../../utils';
import { VIEW_TYPE } from '../../data';
import { LEAD_DETAILS } from '../../types';

interface IBASIC_FORM {
  changeView: (viewType: VIEW_TYPE) => void;
  selectedAnswers: Record<string, string[]>;
  leadDetails: LEAD_DETAILS;
  showTitle?: boolean;
}

export const BasicForm = ({ selectedAnswers }: IBASIC_FORM): JSX.Element => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [formData, setFormData] = useState<Record<string, string>>({
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({
    email: '',
    phone: '',
  });

  const [formSuccess, setFormSuccess] = useState(false);

  const validateField = (fieldName: string, value: string) => {
    let errorMessage = '';

    switch (fieldName) {
      case 'email':
        errorMessage = emailValidator(value, fieldName);
        break;
      case 'phone':
        errorMessage = phoneValidator(value, fieldName);
        break;
      default:
        break;
    }

    setErrors({
      ...errors,
      [fieldName]: errorMessage,
    });
  };

  const IsFormValid = () =>
    formData.email && formData.phone && !errors.email && !errors.phone
      ? true
      : false;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    validateField(name, value);
  };

  const submitButtonClicked = async (
    e: React.MouseEvent | React.TouchEvent
  ) => {
    const submitButton = e.target as HTMLButtonElement;
    const { email, phone } = formData;
    handleTrack({
      label: `qna_modal_${submitButton.textContent}`,
      valueObj: { ...selectedAnswers, email: email, phone_no: phone },
    });

    if (IsFormValid()) {
      submitButton.setAttribute('disabled', 'true');
      submitButton.innerText = 'Submitting...';

      try {
        const QnALeadData = {
          email: email || '',
          phone_number: phone || '',
          lead_source_url: window.location.href || '',
          form_type: 'pricing-plan-selector-form',
          lead_type: 'plan-selector-leads',
          note: 'Lead from Plan Selector Form',
          leadType: 'plan-selector-leads', //TODO: is this required? as we already have lead_type
          lead_source_page: window.location.href || '',
          lead_creation_time: Math.floor(new Date().getTime() / 1000),
          type_name: 'CA',
        };
        const token = await recaptchaRef.current?.executeAsync();

        await sendPricingPageLead({
          formData: QnALeadData,
          token: token as string,
        });
        setFormSuccess(true);
      } catch (err) {
        console.log('Error submitting form', err);
      }

      submitButton.setAttribute('disabled', 'false');
      submitButton.innerText = 'Request a callback';
    }
  };

  const inputFields = [
    {
      name: 'phone',
      label: 'Phone Number',
      placeholder: 'Enter your phone no',
    },
    { name: 'email', label: 'Email ID', placeholder: 'Enter your email id' },
  ];

  return (
    <>
      <div className="w-full sm:px-0 mx-auto">
        {!formSuccess ? (
          <div id="callback-modal-wrapper">
            <form
              className="mt-4 contact-sales-form"
              data-identifier="callback-modal"
            >
              <div className="w-full">
                {inputFields.map((field) => (
                  <div key={field.name} className="pb-4">
                    <label
                      htmlFor={field.name}
                      className="text-base text-font-200 font-medium sm:text-s-14"
                    >
                      {field.label}
                    </label>
                    <br />
                    <input
                      placeholder={field.placeholder}
                      name={field.name}
                      value={formData[field.name]}
                      type="text"
                      className="border border-gray-200 rounded-sm mt-1 text-base w-full h-12 px-2 placeholder-gray-400 placeholder-opacity-75 sm:text-s-14 sm:h-10"
                      onChange={handleOnChange}
                      onBlur={handleOnChange}
                    />
                    {errors[field.name] && (
                      <span
                        data-testid={`${field.name}-error`}
                        className="inline-block text-red-350 text-s-10 font-semibold"
                      >
                        {errors[field.name]}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <Button
                disabled={!IsFormValid()}
                onClick={submitButtonClicked}
                className={`mt-4 bg-transparent border border-font-500 text-font-500 font-bold sm:w-full ${
                  !IsFormValid() ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                Request a Callback
              </Button>
            </form>
          </div>
        ) : (
          <div
            id="callback-success-wrapper"
            className="text-center my-10  border rounded-lg px-6 py-8"
          >
            <p className="text-base font-bold text-font-500">
              Thank you for your response
            </p>
            <p id="success-message" className="text-base text-font-500">
              Our representative will get in touch with you shortly.
            </p>
          </div>
        )}

        <ReCAPTCHA
          sitekey="6LevgAErAAAAAKzR-MPcdXwti7lpxV3-jOPM0vL2"
          size="invisible"
          ref={recaptchaRef}
        />
      </div>
    </>
  );
};
